import { ContactMethod } from 'components/ContactInfo';
import { ExternalLinkButtonProps } from 'components/ExternalLinkButton';
import { TableType } from 'crono-fe-common/types/externalProperty';
import { checkIfValidWebsite, withProtocol } from 'crono-fe-common/utils';
import LinkedinUrlUtils from 'crono-fe-common/utils/LinkedinUrlUtils';
import {
  checkIfValidLinkedinCompanyUrl,
  checkIfValidLinkedinProfileUrl,
} from 'utils/validationUtils';

/** Whether a form value counts as not filled in; whitespace-only text is blank. */
export const isBlankValue = (value: unknown) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
};

/** The text without its surrounding spaces, or null when nothing is left. */
export const trimmedOrNull = (value: string) => value.trim() || null;

/** The whole number typed in a field, or null when there's none. */
export const toNumberOrNull = (value: string) => {
  const number = Number.parseInt(value, 10);
  return Number.isNaN(number) ? null : number;
};

const describeFields = (labels: string[], problem: string) =>
  labels.length === 1
    ? `Field "${labels[0]}" is ${problem}`
    : `Fields ${labels.map((l) => `"${l}"`).join(', ')} are ${problem}`;

/**
 * The error shown when a creation form is submitted with mandatory fields left
 * blank or with addresses that aren't valid, both in one sentence.
 */
export const getFieldsError = ({
  missing = [],
  invalid = [],
}: {
  missing?: string[];
  invalid?: string[];
}) => {
  const problems = [
    missing.length > 0 && describeFields(missing, 'mandatory'),
    invalid.length > 0 && describeFields(invalid, 'not valid'),
  ].filter(Boolean);
  return problems.join('. ');
};

/** A field's label, starred when the field is mandatory. */
export const getFieldLabel = (label: string, required = false) =>
  required && label ? `${label}*` : label;

/** A placeholder that ends with a star when the field is mandatory, with an ellipsis otherwise. */
export const getFieldPlaceholder = (placeholder: string, required = false) =>
  `${placeholder}${required ? '*' : '...'}`;

/** Whose contact method it is: a LinkedIn URL is a company page or a person's profile. */
export type ContactMethodOwner = 'company' | 'contact';

/** The contact methods whose value is a URL. */
export type ContactMethodUrl = ContactMethod.Linkedin | ContactMethod.Website;

const isContactMethodUrl = (
  method: ContactMethod,
): method is ContactMethodUrl =>
  method === ContactMethod.Linkedin || method === ContactMethod.Website;

const isValidCompanyLinkedinUrl = (url: string) =>
  checkIfValidLinkedinCompanyUrl(url) ||
  LinkedinUrlUtils.getNumericIdFromSalesNavCompanyUrl(url)[0] !== null;

const isValidUrl = (
  method: ContactMethodUrl,
  owner: ContactMethodOwner,
  url: string,
) => {
  if (method === ContactMethod.Website) {
    return checkIfValidWebsite(withProtocol(url));
  }
  return owner === 'company'
    ? isValidCompanyLinkedinUrl(url)
    : checkIfValidLinkedinProfileUrl(url);
};

/**
 * Whether a LinkedIn or website value can be saved: blank, or a valid address
 * (a LinkedIn profile or company page, Sales Navigator's included).
 */
export const isValidContactUrl = (
  method: ContactMethodUrl,
  owner: ContactMethodOwner,
  value: string,
) => {
  const url = value.trim();
  return !url || isValidUrl(method, owner, url);
};

const getContactUrlLabel = (
  method: ContactMethodUrl,
  owner: ContactMethodOwner,
) => {
  if (method === ContactMethod.Website) return `Open ${owner} website`;
  return owner === 'company'
    ? 'Open company LinkedIn'
    : 'Open LinkedIn profile';
};

/**
 * The link that opens a LinkedIn or website value in a new tab, once the value
 * is a valid address; null for other contact methods.
 */
export const getContactMethodLink = (
  method: ContactMethod,
  owner: ContactMethodOwner,
  value: string,
): ExternalLinkButtonProps | null => {
  const url = value.trim();
  if (!url || !isContactMethodUrl(method) || !isValidUrl(method, owner, url)) {
    return null;
  }
  return { href: url, label: getContactUrlLabel(method, owner) };
};

/** "1 contact", "3 companies"… for the records a creation form adds. */
export const getRecordsLabel = (tableType: TableType, count: number) => {
  const plural = count !== 1;

  let noun: string;
  if (tableType === 'Account') {
    noun = plural ? 'companies' : 'company';
  } else if (tableType === 'Lead') {
    noun = plural ? 'leads' : 'lead';
  } else {
    noun = plural ? 'contacts' : 'contact';
  }

  return `${count} ${noun}`;
};
